import { statusMeta, astrologerDisplayName } from './bookingDetailsData'

/* Small presentational pieces shared by the seeker-side BookingDetailsPage
   and the astrologer-side AppointmentDetailsPage. */

export function StatusPill({ status }) {
  const meta = statusMeta(status)
  return (
    <span className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${meta.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {meta.label}
    </span>
  )
}

/**
 * Full-bleed dark hero band — same idiom as BookingHeroSection. The app's
 * header is transparent (white text) until scroll, so detail pages need this
 * dark canvas at the top for the header to stay readable in its normal state.
 */
export function DetailHero({ backHref, backLabel, title, code, status, facts = [], onCopy, copied, action }) {
  return (
    <div className="relative overflow-hidden bg-primary text-white pt-32 pb-10">
      <div aria-hidden="true" className="absolute inset-0 bg-linear-to-b from-[#3a0a14] via-primary to-[#2a0710]" />
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-linear(55%_70%_at_50%_0%,rgba(232,119,15,0.20),transparent_70%)]" />
      <div
        aria-hidden="true"
        className="absolute -right-12 top-1/2 -translate-y-1/2 opacity-[0.08] pointer-events-none select-none"
      >
        <span className="text-[150px] text-[#E6C878]">ॐ</span>
      </div>

      <div className="container px-6 mx-auto relative z-10">
        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mb-6">
          <a href={backHref} className="hover:text-secondary transition-colors">{backLabel}</a>
          <span className="opacity-40">/</span>
          <span className="text-secondary">{code}</span>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-cinzel leading-tight">{title}</h1>
            <p className="text-white/50 text-[11px] uppercase tracking-widest mt-2.5 flex items-center gap-2">
              Booking ID · <span className="text-white font-semibold">{code}</span>
              {onCopy && (
                <button
                  type="button"
                  onClick={onCopy}
                  className="text-secondary hover:text-white transition-colors normal-case"
                  title="Copy booking ID"
                >
                  {copied ? '✓ Copied' : '⧉ Copy'}
                </button>
              )}
            </p>
          </div>
          {action ?? (status && <StatusPill status={status} />)}
        </div>

        {facts.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-8">
            {facts.map((fact) => (
              <div key={fact.label} className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 py-2.5 min-w-32">
                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40">{fact.label}</p>
                <p className="text-sm font-semibold text-white mt-1">{fact.value ?? '—'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** Gold-hairline card — the app's premium section shell. */
export function SectionCard({ title, icon, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-[#C8922A]/15 shadow-sm overflow-hidden ${className}`}>
      <div className="h-0.75 w-full bg-linear-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]/20" />
      <div className="p-6 sm:p-7">
        {title && (
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C8922A] mb-4">
            {icon && <span className="mr-1.5">{icon}</span>}
            {title}
          </p>
        )}
        {children}
      </div>
    </div>
  )
}

export function FactRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] shrink-0 pt-0.5">{label}</span>
      <span className="text-sm font-semibold text-[#1C140A] text-right min-w-0 wrap-break-word">{value ?? '—'}</span>
    </div>
  )
}

/**
 * The finalized astrologer note, rendered as a letterhead document — the
 * seeker-facing "prescription" view of the consultation summary.
 */
export function NoteDocument({ booking }) {
  const note = String(booking?.astrologer_note ?? '').trim()
  const paragraphs = note.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
  return (
    <div className="rounded-2xl border border-[#C8922A]/20 bg-[#FDFBF7] p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b-2 border-[#C8922A]/20">
        <div>
          <p className="font-cinzel text-base text-[#1C140A] uppercase tracking-wide">Astrologer Consultation Notes</p>
          <p className="text-sm font-semibold text-[#1C140A] mt-1">{astrologerDisplayName(booking)}</p>
          <p className="text-xs text-[#8B7355] mt-0.5">Consultation summary, remedies and post-session guidance</p>
        </div>
        <div className="text-right text-[11px] text-[#8B7355]">
          <p className="font-bold uppercase tracking-widest text-[#1C140A]">Consultation Record</p>
          <p className="mt-1">Reference: BKNG{booking?.id}</p>
        </div>
      </div>
      <p className="font-cinzel text-xl text-[#1C140A] mt-5 mb-3">Astrological Advice</p>
      {paragraphs.length > 0 ? (
        <div className="space-y-3 text-sm leading-7 text-[#403c3d]">
          {paragraphs.map((p, i) => (
            <p key={i} className="whitespace-pre-line">{p}</p>
          ))}
        </div>
      ) : (
        <p className="text-sm italic text-[#8B7355]">No astrologer note was provided for this appointment.</p>
      )}
      <div className="mt-8 pt-4 border-t border-[#C8922A]/15 text-right text-[11px] text-[#8B7355]">
        <p className="text-sm font-semibold text-[#1C140A]">{astrologerDisplayName(booking)}</p>
        <p className="mt-0.5">Reference: BKNG{booking?.id}</p>
      </div>
    </div>
  )
}

/**
 * Uploaded face/palm photos, shown as a labeled thumbnail grid — each opens
 * full-size in a new tab. Renders nothing when there are no photos.
 */
export function PhotoGallery({ photos }) {
  if (!photos?.length) return null
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      {photos.map((photo) => (
        <a
          key={photo.key}
          href={photo.url}
          target="_blank"
          rel="noreferrer"
          className="group block rounded-xl overflow-hidden border border-[#C8922A]/15 bg-[#FDFBF7]"
        >
          <img
            src={photo.url}
            alt={photo.label}
            loading="lazy"
            className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355] text-center py-2">
            {photo.label}
          </p>
        </a>
      ))}
    </div>
  )
}

/** Body placeholders — rendered below the DetailHero, so no top offset. */
export function DetailSkeleton() {
  return (
    <div className="container px-6 mx-auto pt-10 pb-24 animate-pulse">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-6">
          <div className="h-40 bg-white rounded-2xl border border-stone-100" />
          <div className="h-56 bg-white rounded-2xl border border-stone-100" />
          <div className="h-48 bg-white rounded-2xl border border-stone-100" />
        </div>
        <div className="lg:col-span-4 space-y-6">
          <div className="h-40 bg-white rounded-2xl border border-stone-100" />
          <div className="h-36 bg-white rounded-2xl border border-stone-100" />
        </div>
      </div>
    </div>
  )
}

export function DetailError({ id, onRetry, backHref, backLabel }) {
  return (
    <div className="pt-16 pb-24 text-center px-6">
      <div className="text-5xl mb-4 opacity-40">🛰️</div>
      <p className="font-cinzel text-lg font-bold mb-1 text-[#1C140A]">Couldn&rsquo;t load booking BKNG{id}</p>
      <p className="text-sm mb-6 text-[#8B7355]">It may belong to another account, or something went wrong.</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
          style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
        >
          Retry
        </button>
        <a
          href={backHref}
          className="hero-btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-[10px] font-bold uppercase tracking-widest"
        >
          {backLabel}
        </a>
      </div>
    </div>
  )
}
